import { BookingStateEnum } from '@xeetr/types/enums'
import { FoodBookingModel, ServiceBookingModel, ViewFoodEvent } from '@xeetr/types/models'
import { Pen } from 'lucide-react'
import { useContext, useState } from 'react'

import { ActiveTenantContext } from '../../../context/common/Context'
import { useAbsences } from '../../../hooks/useAbsences'
import { useAuth } from '../../../hooks/useAuth'
import { useEmployees } from '../../../hooks/useEmployees'
import { useEvents } from '../../../hooks/useEvents'
import { useSidebar } from '../../../hooks/useSidebar'
import { FoodEventService } from '../../../services/FoodEvent.service'
import { CustomEvents } from '../../../types/AnalyticsKeys'
import { SidebarType } from '../../../types/Sidebar'
import { parseAbsenceBookingId, parseEventBookingId } from '../../../utils/bookingIds'
import TrackedButton from '../../commons/TrackedButton'

import DeleteDialogContent from './common/DeleteDialogContent'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export const EditColumn = (booking: FoodBookingModel | ServiceBookingModel) => {
  const { setSidebar } = useSidebar()
  const { tenantId } = useContext(ActiveTenantContext)

  const { event, get: getEvent, remove: removeEvent, isLoading: eventLoading, events } = useEvents()
  const { remove: deleteAbsence, absences } = useAbsences()
  const { employees } = useEmployees()
  const { getAccessTokenSilently } = useAuth()

  const [deleteModalVisible, setDeleteModalVisible] = useState(false)

  const buttonDefaults = {
    variant: 'outline' as const,
    size: 'sm' as const,
  }

  const handleAccept = async () => {
    switch (booking.state?.status) {
      case BookingStateEnum.ABSENCE:
        try {
          const parsed = parseAbsenceBookingId(booking.id)
          if (parsed) {
            deleteAbsence(parsed.absenceId)
          }
        } catch (error) {
          console.error('Failed to delete absence:', error)
        }
        break
      case BookingStateEnum.EVENT:
        const parsedEvent = parseEventBookingId(booking.id)
        if (parsedEvent) {
          getEvent(parsedEvent.eventId)
          if (event) removeEvent(event)
        }
        break
      default:
        break
    }
  }

  const renderButton = () => {
    switch (booking.state?.status) {
      case BookingStateEnum.ABSENCE:
        return (
          <TrackedButton
            {...buttonDefaults}
            trackingkey={CustomEvents.OPEN_DELETE_ABSENCE_MODAL}
            icon={Pen}
            tooltip="Abwesenheit bearbeiten"
            severity="warning"
            onClick={() => {
              const parsed = parseAbsenceBookingId(booking.id)
              if (parsed) {
                const absence = absences.find((a) => a.id === parsed.absenceId)
                if (absence) {
                  setSidebar({
                    open: true,
                    type: SidebarType.ABSENCE_EDIT,
                    absence: absence,
                  })
                }
              }
            }}
          />
        )
      case BookingStateEnum.EVENT:
        return (
          <TrackedButton
            {...buttonDefaults}
            trackingkey={CustomEvents.OPEN_EDIT_EVENT_MODAL}
            icon={Pen}
            tooltip="Event bearbeiten"
            severity="warning"
            onClick={() => {
              const parsed = parseEventBookingId(booking.id)
              if (parsed) {
                const event = events.find((e) => e.id === parsed.eventId)
                if (event) {
                  setSidebar({
                    open: true,
                    type: SidebarType.EVENT_EDIT,
                    event,
                  })
                } else {
                  // Fallback to API call if not found in context
                  getAccessTokenSilently().then((token) => {
                    const foodEventService = new FoodEventService(token, tenantId)
                    foodEventService
                      .getFoodEvent(parsed.eventId)
                      .then((event: ViewFoodEvent) => {
                        setSidebar({
                          open: true,
                          type: SidebarType.EVENT_EDIT,
                          event,
                        })
                      })
                      .catch((error) => {
                        console.error('Failed to fetch event data:', error)
                      })
                  })
                }
              }
            }}
          />
        )
      case BookingStateEnum.HOLIDAY:
        return <></>
      default:
        return (
          <TrackedButton
            {...buttonDefaults}
            trackingkey={CustomEvents.OPEN_BOOKING_MODAL_EDIT}
            icon={Pen}
            tooltip="Reservierung bearbeiten"
            severity="warning"
            onClick={() =>
              setSidebar({
                open: true,
                type: SidebarType.BOOKING_EDIT,
                booking,
              })
            }
          />
        )
    }
  }

  return (
    <>
      <AlertDialog open={deleteModalVisible} onOpenChange={setDeleteModalVisible}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Löschen bestätigen!</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            <DeleteDialogContent
              booking={booking}
              data={
                booking.state?.status === BookingStateEnum.ABSENCE
                  ? employees
                      ?.flatMap((employee) => employee.absences || [])
                      ?.find((absence) => absence.id === booking.id)
                  : event
              }
              isLoading={booking.state?.status === BookingStateEnum.ABSENCE ? false : eventLoading}
              getEventDetails={(id) => getEvent(id)}
              getEmployeeAbsence={() => {}} // Not needed anymore since we get data from context
            />
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteModalVisible(false)}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleAccept()
                setDeleteModalVisible(false)
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {renderButton()}
    </>
  )
}
